import type { PortalIdentityDto } from '@erp-boilerplate/types';
import { create } from 'zustand';

import { setAccessToken, setRefreshEndpoint } from '@/lib/api-client';
import { queryClient } from '@/lib/query-client';
import { portalAuthService } from '@/services/portal.service';

/**
 * The customer portal session (Phase 6).
 *
 * A separate store from the ERP's `auth.store`, and deliberately so. They hold
 * different principals (a customer, not a staff user), consume different
 * endpoints, and must never be confused for one another by a component. Sharing
 * one store would mean every screen asking "which kind of session is this?".
 *
 * What they *do* share is the API client, and therefore the in-memory access
 * token — which is correct, because a browser is signed into one or the other,
 * never both. The refresh endpoint is switched here on bootstrap so the client
 * rotates against the portal's own path-scoped cookie.
 *
 * The access token lives in the API client module, never in this store, so it is
 * not serialised into devtools. The refresh token is an httpOnly cookie the
 * JavaScript never sees.
 */

export type PortalAuthStatus = 'initialising' | 'authenticated' | 'unauthenticated';

interface PortalAuthState {
  status: PortalAuthStatus;
  customer: PortalIdentityDto | null;

  /** Restore a session from the httpOnly portal cookie on app start. */
  bootstrap: () => Promise<void>;
  login: (identifier: string, password: string, rememberMe?: boolean) => Promise<PortalIdentityDto>;
  logout: () => Promise<void>;
  endSession: () => void;
}

export const usePortalAuthStore = create<PortalAuthState>((set, get) => ({
  status: 'initialising',
  customer: null,

  bootstrap: async () => {
    // Point the shared client at the portal's rotation endpoint before the first
    // request. A staff cookie is not transmitted to this path, so a browser
    // signed into the ERP simply finds no portal session — which is right.
    setRefreshEndpoint('/portal/auth/refresh');

    try {
      const { customer, tokens } = await portalAuthService.refresh();
      setAccessToken(tokens.accessToken);
      set({ status: 'authenticated', customer });
    } catch {
      // No cookie, or it has expired — a normal first visit.
      setAccessToken(null);
      set({ status: 'unauthenticated', customer: null });
    }
  },

  login: async (identifier, password, rememberMe = false) => {
    setRefreshEndpoint('/portal/auth/refresh');
    const { customer, tokens } = await portalAuthService.login({
      identifier,
      password,
      rememberMe,
    });
    setAccessToken(tokens.accessToken);
    // A cache left over from a previous customer on a shared device must not
    // survive a sign-in: the next screen would render someone else's documents.
    queryClient.clear();
    set({ status: 'authenticated', customer });
    return customer;
  },

  logout: async () => {
    try {
      await portalAuthService.logout();
    } finally {
      // Sign out locally even if the request failed — the customer asked to leave.
      setAccessToken(null);
      set({ status: 'unauthenticated', customer: null });
      queryClient.clear();
    }
  },

  endSession: () => {
    if (get().status === 'unauthenticated') return;
    setAccessToken(null);
    set({ status: 'unauthenticated', customer: null });
    queryClient.clear();
  },
}));
